/**
 * Animation runner — turns a generator into a materialized step list.
 *
 * Algorithms are written as `function*` and yield discriminated step objects.
 * The runner walks the generator to completion and wraps each yielded step
 * in a `StepEnvelope` that includes its index and a narration string.
 *
 * Step counts in this project are bounded and small (~5–200 steps), so
 * eager materialization is the right call. Lazy iteration is a future
 * optimization if we ever animate algorithms with massive step counts.
 */

import type { StepEnvelope } from './types';

export function materialize<S>(
  gen: Generator<S>,
  describe: (step: S, index: number) => string,
): StepEnvelope<S>[] {
  const out: StepEnvelope<S>[] = [];
  let index = 0;
  for (const step of gen) {
    out.push({
      index,
      step,
      narration: describe(step, index),
    });
    index += 1;
  }
  return out;
}
